import { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { Button, cn } from "@carry/ui";
import { formatYen, itemsTotal, lineSubtotal } from "@carry/shared";
import { BottomBar } from "../components/BottomBar";
import { Header } from "../components/Header";
import { IncompleteStopBackfillForm } from "../components/IncompleteStopBackfillForm";
import { MapLink } from "../components/MapLink";
import { useDriverOrderDocumentMeta, type DriverFetcher } from "../hooks";
import { buildGoogleMapsUrl, type OpenExternalUrl } from "../lib/map-link";
import type { DriverStop } from "../lib/normalize";

const StopMap = lazy(() => import("../components/StopMap"));

type DetailTab = "pickup" | "delivery" | "product";

const DETAIL_TABS: ReadonlyArray<{ key: DetailTab; label: string }> = [
  { key: "pickup", label: "集荷情報" },
  { key: "delivery", label: "納品情報" },
  { key: "product", label: "商品情報" },
];

function MapArea({ stop, collect }: { stop: DriverStop; collect: boolean }) {
  return (
    <Suspense
      fallback={<div className="mb-2.5 h-[140px] animate-pulse rounded-lg bg-[var(--bg-raised)]" />}
    >
      <StopMap lat={stop.lat ?? null} lng={stop.lng ?? null} collect={collect} />
    </Suspense>
  );
}

function InfoRow({
  label,
  value,
  tel,
  mapUrl,
  openExternalUrl,
}: {
  label: string;
  value?: string | null;
  tel?: boolean;
  /** 指定するとその値を Google マップを開くリンクにする (住所行で使用) */
  mapUrl?: string | null;
  openExternalUrl?: OpenExternalUrl;
}) {
  return (
    <>
      <div className="min-w-0 break-words text-[var(--ink-3)]">{label}</div>
      {tel && value ? (
        <a
          href={`tel:${value}`}
          className="inline-flex min-h-11 min-w-0 items-center break-all font-medium text-[var(--info)]"
        >
          {value}
        </a>
      ) : mapUrl && value ? (
        <MapLink
          url={mapUrl}
          openExternalUrl={openExternalUrl}
          className="inline-flex min-h-11 min-w-0 items-center break-words font-medium text-[var(--info)] underline"
        >
          {value}
        </MapLink>
      ) : (
        <div className="min-w-0 break-words">{value ?? "—"}</div>
      )}
    </>
  );
}

function NoteBlock({ note }: { note: string }) {
  return (
    <div className="mb-2.5 flex gap-2 rounded-lg bg-[var(--st-assigned-bg)] px-3 py-2.5">
      <span className="text-[length:var(--fs-md)]">⚠</span>
      <div className="min-w-0 break-words text-[length:var(--fs-sm)] leading-relaxed text-[var(--st-assigned-ink)]">
        {note}
      </div>
    </div>
  );
}

function CautionBlock({ note }: { note: string }) {
  return (
    <div className="mb-2.5 flex gap-2 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2.5">
      <span className="text-[length:var(--fs-md)]">🚨</span>
      <div className="min-w-0 break-words text-[length:var(--fs-sm)] font-semibold leading-relaxed text-[var(--st-cancel-ink)]">
        {note}
      </div>
    </div>
  );
}

/** 座標優先・住所フォールバックで Google マップを開く CTA。どちらも無い stop では出さない。 */
function NavButton({
  url,
  openExternalUrl,
}: {
  url: string | null;
  openExternalUrl?: OpenExternalUrl;
}) {
  if (!url) return null;
  return (
    <MapLink
      url={url}
      openExternalUrl={openExternalUrl}
      className="mb-2.5 flex min-h-11 items-center justify-center rounded-lg border border-[var(--info)] bg-[var(--bg-raised)] px-3 py-2.5 text-[length:var(--fs-sm)] font-medium text-[var(--info)]"
    >
      マップで開く
    </MapLink>
  );
}

export function StopDetail({
  stop,
  acting,
  error,
  fetcher,
  onBack,
  onDepart,
  onAdvance,
  onComplete,
  onDelay,
  onShowDocument,
  onAddExpense,
  onView,
  onRevert,
  onEditTimestamp,
  onShowProof,
  backfill,
  onEditOdometer,
  onCancel,
  departDisabledReason,
  openExternalUrl,
  dateLabel,
  readOnly = false,
}: {
  stop: DriverStop;
  acting: boolean;
  error: string | null;
  /**
   * #768: ヘッダーに出す表示日 ("8/17 (月)")。今日を見ているときは未指定にする。
   * 詳細画面には日付ストリップが無いので、過去日を今日と取り違えないための手掛かりにする。
   */
  dateLabel?: string;
  /** 閲覧専用 (今日以外の日など)。打刻/取消/遅延/オドメーター入力など更新系の操作を一切表示しない。 */
  readOnly?: boolean;
  /**
   * 注文書実体の有無 (has_document) を meta API で確認するための fetcher。
   * 省略時は注文番号有無で従来どおりフォールバック判定する。
   */
  fetcher?: DriverFetcher;
  onBack: () => void;
  /** 出発打刻 (status: pending → in_transit)。pending の stop でのみ表示される */
  onDepart?: () => void;
  /** 到着打刻 (status: in_transit → arrived) */
  onAdvance: () => void;
  /** 完了打刻 (status: arrived → completed)。到着打刻済みのときのみ表示される */
  onComplete?: () => void;
  onDelay: () => void;
  onShowDocument: () => void;
  /**
   * #616: この配達先の経費 (高速代・駐車場代) を登録する。
   * 未指定 = 導線を出さない (閲覧専用タブ・出勤前など)。
   */
  onAddExpense?: () => void;
  /**
   * F04 API-38: 配送指示詳細を開いた時点で当該ルートの指示を「確認済み」として記録する。
   * fire-and-forget・冪等。マウント時に 1 回だけ呼ぶ。
   */
  onView?: () => void;
  /** F14 Slice 3: 完了済 (done) のとき表示する補助操作 */
  onRevert?: () => void;
  onEditTimestamp?: () => void;
  /** F14 Slice 4: 完了済 (done) のとき表示する証跡画像 upload 画面遷移 */
  onShowProof?: () => void;
  /**
   * #839: 過去日の未完了実績を、この画面からその場で後追い登録する。
   * 未指定 = 導線を出さない (当日・未来日・既に終端の stop)。
   */
  backfill?: {
    /** 対象日 (YYYY-MM-DD)。 */
    workDate: string;
    fetcher: DriverFetcher;
    /** 保存成功時に呼ぶ (呼び出し側で stop 一覧を再取得する想定)。 */
    onSaved: () => void | Promise<void>;
  };
  /** F29 委託先支払距離: 配達先で到着オドメーターを後から入力・修正する (到着済み/完了済みでも可) */
  onEditOdometer?: () => void;
  /** 未完了 stop を業務判断で取消する。理由は呼び出し側が audit/status update に渡す。 */
  onCancel?: (reason: string) => void;
  /** 別 stop が進行中など、出発を止める理由 */
  departDisabledReason?: string | null;
  /**
   * LIFF 内から外部ブラウザ / 地図アプリを開く関数 (`liff.openWindow({ external: true })`)。
   * 未指定なら通常の別タブ遷移にフォールバックする。
   */
  openExternalUrl?: OpenExternalUrl;
}) {
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const isCollect = stop.kind === "collect";
  // 経由地 (#759) も集荷側の立寄りなので、詳細は集荷タブから開く。
  const isPickupKind =
    stop.kind === "pickup" || stop.kind === "collect" || stop.kind === "waypoint";
  const [tab, setTab] = useState<DetailTab>(isPickupKind ? "pickup" : "delivery");
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const tabsId = useId();
  const hasItems = Boolean(stop.items?.length);
  // 合計は単価が入っている行だけの部分和 (Issue #775)。算出規約は admin の受注詳細と共通で、
  // `@carry/shared` に集約している。1 行でも単価が欠けたら出さない、という扱いはしない
  // (単価未入力の行は表内に `未設定` として並ぶので、部分和であることは画面から読み取れる)。
  const totalPrice = itemsTotal(stop.items ?? []);

  const { hasDocument, loading: docMetaLoading } = useDriverOrderDocumentMeta({
    apiBaseUrl: fetcher?.apiBaseUrl ?? "",
    session: fetcher?.session ?? null,
    fetchImpl: fetcher?.fetchImpl,
    stopPublicId: fetcher ? stop.id : null,
  });
  const canShowDocument = hasDocument ?? Boolean(stop.orderNumber);
  const docMetaPending = Boolean(fetcher) && hasDocument === null && docMetaLoading;

  const onViewRef = useRef(onView);
  onViewRef.current = onView;
  useEffect(() => {
    onViewRef.current?.();
  }, [stop.id]);
  useEffect(() => {
    setCancelOpen(false);
    setCancelReason("");
    setTab(isPickupKind ? "pickup" : "delivery");
  }, [stop.id, stop.kind]);

  const selectTab = (nextTab: DetailTab, focus = false) => {
    setTab(nextTab);
    if (focus) {
      const nextIndex = DETAIL_TABS.findIndex(({ key }) => key === nextTab);
      tabRefs.current[nextIndex]?.focus();
    }
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % DETAIL_TABS.length;
    if (event.key === "ArrowLeft")
      nextIndex = (index - 1 + DETAIL_TABS.length) % DETAIL_TABS.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = DETAIL_TABS.length - 1;
    if (nextIndex == null) return;

    event.preventDefault();
    const nextTab = DETAIL_TABS[nextIndex];
    if (nextTab) selectTab(nextTab.key, true);
  };

  // apiStatus (実 API 由来) でステータス更新 CTA の出し分け:
  //  - pending → 出発打刻 (in_transit 化)
  //  - in_transit → 到着打刻
  //  - arrived → 引渡し/回収完了
  //  - completed / done → CTAなし
  // apiStatus が未設定 (モックデータ等) は status で代替する。
  // 出発打刻と到着打刻は排他 (pending↔in_transit で入れ替わる)。
  const showDepartButton =
    !readOnly &&
    Boolean(onDepart) &&
    (stop.apiStatus === "pending" || (!stop.apiStatus && stop.status === "pending"));
  const showArriveButton =
    !readOnly && (stop.apiStatus === "in_transit" || (!stop.apiStatus && stop.status === "active"));
  const showCompleteButton = !readOnly && stop.apiStatus === "arrived" && Boolean(onComplete);
  const canTransitionToCancel =
    stop.apiStatus === "in_transit" ||
    stop.apiStatus === "arrived" ||
    (!stop.apiStatus && stop.status === "active");
  const canCancel =
    !readOnly &&
    Boolean(onCancel) &&
    canTransitionToCancel &&
    stop.status !== "done" &&
    stop.status !== "cancelled" &&
    stop.apiStatus !== "completed" &&
    stop.apiStatus !== "skipped" &&
    stop.apiStatus !== "cancelled";
  const showBottomBar = showDepartButton || showArriveButton || showCompleteButton || canCancel;

  const actualText = `${stop.time}${stop.actual ? ` / ${stop.actual}` : ""}`;
  // Issue #611: 座標があれば座標、無ければ住所文字列で Google マップを開く
  const mapUrl = buildGoogleMapsUrl({ lat: stop.lat, lng: stop.lng, address: stop.address });

  return (
    <div
      className={cn(
        "min-h-screen bg-[var(--bg)]",
        showBottomBar
          ? canCancel
            ? "pb-[var(--stacked-bottom-bar-clearance)]"
            : "pb-[var(--bottom-bar-clearance)]"
          : "pb-6",
      )}
    >
      <Header
        title={stop.name}
        sub={[dateLabel, isCollect ? "食器回収" : null, `予定 ${stop.time}`]
          .filter((part): part is string => !!part)
          .join(" · ")}
        showBack
        onBack={onBack}
      />

      <div
        role="tablist"
        aria-label="配送情報"
        className="flex border-b border-[var(--rule-soft)] bg-[var(--bg-raised)]"
      >
        {DETAIL_TABS.map(({ key, label }, index) => {
          const selected = tab === key;
          return (
            <button
              key={key}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              id={`${tabsId}-${key}-tab`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${tabsId}-${key}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => selectTab(key)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              className={cn(
                "min-h-11 flex-1 px-1 py-2.5 text-[length:var(--fs-sm)] font-medium",
                selected
                  ? "border-b-2 border-[var(--ink)] text-[var(--ink)]"
                  : "text-[var(--ink-3)]",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div className="px-2.5 py-3">
        <div
          id={`${tabsId}-pickup-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-pickup-tab`}
          hidden={tab !== "pickup"}
        >
          {tab === "pickup" && (
            <>
              <MapArea stop={stop} collect={isCollect} />
              <NavButton url={mapUrl} openExternalUrl={openExternalUrl} />
              <div className="mb-2.5 rounded-lg bg-[var(--bg-raised)] p-3.5 shadow-sm">
                <div className="mb-1 break-words text-[length:var(--fs-md)] font-semibold">
                  {stop.name}
                </div>
                <div className="mb-2.5 break-words text-[length:var(--fs-sm)] text-[var(--ink-2)]">
                  {mapUrl ? (
                    <MapLink
                      url={mapUrl}
                      openExternalUrl={openExternalUrl}
                      className="inline-flex min-h-11 items-center break-words font-medium text-[var(--info)] underline"
                    >
                      {stop.address}
                    </MapLink>
                  ) : (
                    stop.address
                  )}
                </div>
                <div className="grid grid-cols-[88px_1fr] gap-x-2.5 gap-y-2 text-[length:var(--fs-sm)]">
                  <InfoRow label="集荷時間" value={stop.pickupWindow} />
                  <InfoRow label="取引先" value={stop.client} />
                  <InfoRow label="取引先連絡先" value={stop.clientPhone} tel />
                  <InfoRow
                    label="店舗電話"
                    value={stop.storePhone ?? (isPickupKind ? stop.tel : null)}
                    tel
                  />
                  <InfoRow label="予定 / 実績" value={actualText} />
                </div>
              </div>
              {stop.note && <NoteBlock note={stop.note} />}
            </>
          )}
        </div>

        <div
          id={`${tabsId}-delivery-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-delivery-tab`}
          hidden={tab !== "delivery"}
        >
          {tab === "delivery" && (
            <>
              <MapArea stop={stop} collect={isCollect} />
              <NavButton url={mapUrl} openExternalUrl={openExternalUrl} />
              {stop.cautionNotes && <CautionBlock note={stop.cautionNotes} />}
              <div className="mb-2.5 rounded-lg bg-[var(--bg-raised)] p-3.5 shadow-sm">
                <div className="grid grid-cols-[88px_1fr] gap-x-2.5 gap-y-2 text-[length:var(--fs-sm)]">
                  <InfoRow label="お届け時対応" value={stop.handoverLocation} />
                  <InfoRow label="指定時間" value={stop.deliveryWindow} />
                  <InfoRow label="予定 / 実績" value={actualText} />
                  <InfoRow label="注文番号" value={stop.orderNumber} />
                  <InfoRow label="会社名" value={stop.deliverySpotCompany ?? stop.client} />
                  <InfoRow
                    label="住所"
                    value={stop.address}
                    mapUrl={mapUrl}
                    openExternalUrl={openExternalUrl}
                  />
                  <InfoRow label="当日担当者" value={stop.dayOfContactPerson ?? stop.contact} />
                  <InfoRow label="当日連絡先" value={stop.dayOfContactPhone ?? stop.tel} tel />
                  <InfoRow label="取引先連絡先" value={stop.clientPhone} tel />
                  <InfoRow
                    label="到着オドメーター"
                    value={stop.arrivalOdometer != null ? `${stop.arrivalOdometer} km` : null}
                  />
                </div>
                {!readOnly && onEditOdometer && (
                  <button
                    type="button"
                    onClick={onEditOdometer}
                    disabled={acting}
                    className="mt-2.5 min-h-11 w-full rounded-lg border border-[var(--info)] px-3 py-2 text-[length:var(--fs-sm)] font-medium text-[var(--info)] disabled:opacity-50"
                  >
                    {stop.arrivalOdometer != null
                      ? "到着オドメーターを修正"
                      : "到着オドメーターを入力"}
                  </button>
                )}
              </div>
              {(stop.arrivalInstructions || stop.note) && (
                <NoteBlock note={stop.arrivalInstructions ?? stop.note ?? ""} />
              )}
            </>
          )}
        </div>

        <div
          id={`${tabsId}-product-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-product-tab`}
          hidden={tab !== "product"}
        >
          {tab === "product" && (
            <div className="mb-2.5 rounded-lg bg-[var(--bg-raised)] p-3.5 shadow-sm">
              {hasItems ? (
                <div className="mb-3 overflow-x-auto">
                  <table className="w-full min-w-[310px] table-fixed text-[length:var(--fs-xs)]">
                    <thead>
                      <tr className="border-b border-[var(--rule-soft)] text-[var(--ink-3)]">
                        <th className="w-[38%] py-1.5 text-left font-medium">商品名</th>
                        <th className="w-[14%] py-1.5 text-right font-medium">数量</th>
                        <th className="w-[22%] py-1.5 text-right font-medium">単価</th>
                        <th className="w-[26%] py-1.5 text-right font-medium">行金額</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stop.items?.map((item, index) => {
                        const linePrice = lineSubtotal(item);
                        return (
                          <tr
                            key={`${item.product_name}-${index}`}
                            className="border-b border-[var(--rule-soft)] last:border-0"
                          >
                            <td className="break-words py-1.5 pr-1">{item.product_name}</td>
                            <td className="num py-1.5 text-right">{item.quantity}</td>
                            <td className="num py-1.5 text-right">
                              {item.unit_price == null ? "未設定" : formatYen(item.unit_price)}
                            </td>
                            <td className="num py-1.5 text-right">
                              {linePrice == null ? "未設定" : formatYen(linePrice)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-4 text-center text-[length:var(--fs-sm)] text-[var(--ink-3)]">
                  商品情報は配送指示に含まれていません
                </div>
              )}
              <div className="grid grid-cols-[88px_1fr] gap-x-2.5 gap-y-2 text-[length:var(--fs-sm)]">
                {hasItems && (
                  <InfoRow
                    label="合計金額"
                    value={totalPrice == null ? "未算出" : formatYen(totalPrice)}
                  />
                )}
                <InfoRow label="受渡方法" value={stop.handoverLocation} />
                <InfoRow label="支払方法" value={stop.paymentType} />
              </div>
            </div>
          )}
        </div>

        {/* 注文書ボタン */}
        <Button
          type="button"
          variant="outline"
          onClick={onShowDocument}
          disabled={!canShowDocument || docMetaPending}
          className="mb-2.5 w-full"
        >
          {docMetaPending
            ? "注文書を確認中…"
            : canShowDocument
              ? "注文書を表示"
              : "注文書は登録されていません"}
        </Button>

        {/* #616: 高速代・駐車場代は「どの配達で使ったか」が請求・支払の単位になる。
            この配達先に固定した状態で経費フォームを開く。 */}
        {onAddExpense && (
          <Button type="button" variant="outline" onClick={onAddExpense} className="mb-2.5 w-full">
            この配達先の経費を登録
          </Button>
        )}

        {/* #839: 過去日の実績はこの場で後追い登録できる。当日の打刻導線とは分ける。 */}
        {backfill && (
          <div className="mb-2.5 rounded-lg bg-[var(--bg-sunken)] p-3">
            <div className="mb-2 text-[length:var(--fs-xs)] font-semibold text-[var(--ink-3)]">
              過去日の実績
            </div>
            <IncompleteStopBackfillForm
              stopId={stop.id}
              isPickup={stop.kind === "pickup"}
              stopName={stop.name}
              workDate={backfill.workDate}
              fetcher={backfill.fetcher}
              onSaved={backfill.onSaved}
            />
          </div>
        )}

        {/* 完了済みの補助操作 */}
        {!readOnly && stop.status === "done" && (onRevert || onEditTimestamp || onShowProof) && (
          <div className="mb-2.5 rounded-lg bg-[var(--bg-sunken)] p-3">
            <div className="mb-2 text-[length:var(--fs-xs)] font-semibold text-[var(--ink-3)]">
              完了済の操作
            </div>
            <div className="flex flex-col gap-2">
              {onShowProof && (
                <button
                  type="button"
                  onClick={onShowProof}
                  className="text-left text-[length:var(--fs-sm)] font-medium text-[var(--info)] underline"
                >
                  証跡画像 (写真/署名)
                </button>
              )}
              {onEditTimestamp && (
                <button
                  type="button"
                  onClick={onEditTimestamp}
                  className="text-left text-[length:var(--fs-sm)] font-medium text-[var(--info)] underline"
                >
                  打刻修正する
                </button>
              )}
              {onRevert && (
                <button
                  type="button"
                  onClick={onRevert}
                  className="text-left text-[length:var(--fs-sm)] font-medium text-[var(--danger)] underline"
                >
                  キャンセルして集荷中に戻す
                </button>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-2.5 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {error}
          </div>
        )}
      </div>

      {cancelOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-stop-title"
          className="safe-bottom fixed inset-0 z-50 flex items-end bg-black/40 px-3"
        >
          <div className="w-full rounded-lg bg-[var(--bg-raised)] p-4 shadow-lg">
            <h2
              id="cancel-stop-title"
              className="mb-2 text-[length:var(--fs-md)] font-semibold text-[var(--ink-1)]"
            >
              この配送をキャンセルします
            </h2>
            <label
              htmlFor="cancel-stop-reason"
              className="mb-1 block text-[length:var(--fs-xs)] font-medium text-[var(--ink-3)]"
            >
              取消理由
            </label>
            <textarea
              id="cancel-stop-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.currentTarget.value)}
              className="mb-3 min-h-[88px] w-full rounded-lg border border-[var(--rule-soft)] bg-[var(--bg)] p-2 text-[length:var(--fs-sm)]"
              placeholder="例: 納品先不在"
              disabled={acting}
            />
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCancelOpen(false)}
                disabled={acting}
              >
                戻る
              </Button>
              <Button
                type="button"
                onClick={() => onCancel?.(cancelReason.trim())}
                disabled={acting || cancelReason.trim().length === 0}
              >
                取消を確定
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDepartButton && departDisabledReason && (
        <p
          id={`detail-depart-blocked-${stop.id}`}
          className="mx-3 mb-2 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
        >
          {departDisabledReason}
        </p>
      )}

      {showBottomBar && (
        <BottomBar>
          {canCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelOpen(true)}
              disabled={acting}
              className="h-auto min-h-[var(--row-h)] px-4 py-3 text-[length:var(--fs-sm)] font-medium text-[var(--danger)]"
            >
              配送取消
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={onDelay}
            disabled={acting}
            className="h-auto min-h-[var(--row-h)] px-4 py-3 text-[length:var(--fs-sm)] font-medium text-[var(--ink-3)]"
          >
            遅延報告
          </Button>
          {showDepartButton && (
            <Button
              type="button"
              onClick={onDepart}
              disabled={acting || Boolean(departDisabledReason)}
              aria-describedby={
                departDisabledReason ? `detail-depart-blocked-${stop.id}` : undefined
              }
              className={cn(
                "h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-md)] font-semibold",
                "bg-[var(--info)] text-white hover:bg-[var(--info)]/90",
                acting && "cursor-wait opacity-70",
              )}
            >
              {acting ? "送信中…" : "出発打刻"}
            </Button>
          )}
          {showArriveButton && (
            <Button
              type="button"
              onClick={onAdvance}
              disabled={acting}
              className={cn(
                "h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-md)] font-semibold",
                isCollect
                  ? "bg-[var(--status-collect)] text-white hover:bg-[var(--status-collect)]/90"
                  : "bg-[var(--ok)] text-white hover:bg-[var(--ok)]/90",
                acting && "cursor-wait opacity-70",
              )}
            >
              {acting ? "送信中…" : "到着打刻"}
            </Button>
          )}
          {showCompleteButton && (
            <Button
              type="button"
              onClick={onComplete}
              disabled={acting}
              className={cn(
                "h-auto min-h-[var(--row-h)] flex-1 px-4 py-3 text-[length:var(--fs-md)] font-semibold",
                isCollect
                  ? "bg-[var(--status-collect)] text-white hover:bg-[var(--status-collect)]/90"
                  : "bg-[var(--ok)] text-white hover:bg-[var(--ok)]/90",
                acting && "cursor-wait opacity-70",
              )}
            >
              {acting ? "送信中…" : isCollect ? "回収完了" : "引渡し完了"}
            </Button>
          )}
        </BottomBar>
      )}
    </div>
  );
}
